import { tokenCss } from './tokens'

/**
 * Series colours are CSS custom properties rather than literals so the dark
 * steps are selected for the dark surface instead of being derived from the
 * light ones at render time.
 */
export const SERIES_SLOTS = 7

export function seriesColor(slot: number): string {
  return slot >= 0 && slot < SERIES_SLOTS
    ? `var(--nw-series-${slot + 1})`
    : 'var(--nw-series-other)'
}

/**
 * Every selector in this sheet that styles a control, and the one thing each of
 * them has to say.
 *
 * `.nw-btn` shipped two anchor-buttons on `/sources` underlined, because a class
 * that decides what a button looks like does not get to assume it is on a
 * `<button>`. Any of these can be put on an `<a>`, and an `<a>` arrives
 * underlined. Held as a list so the rule is written once and so a test can hold
 * the list against the sheet: a new control declares `cursor: pointer`, and the
 * test says which one is missing here rather than leaving it to be noticed on a
 * page.
 */
export const CONTROL_SELECTORS = [
  '.nw :where(button)',
  '.nw-modal :where(button)',
  '.nw-btn',
  '.nw-dashed',
  '.nw-why',
  '.nw-segmented button',
  '.nw-tabs button',
  '.nw-series button',
  '.nw-menu-trigger',
  '.nw-modal-close',
  ".nw-menu-panel [role='menuitem']",
  ".nw-menu-panel [role='menuitemradio']",
] as const

/**
 * How much of its colour the plot's flat wash lays down, as a percentage, held
 * here so the key that names the wash is mixed from the same number.
 */
export const COVERAGE_WASH_PERCENT = 14

/**
 * No colour is named below. That part is exact and worth saying plainly: no
 * colour is written under `web/src` outside `tokens.ts` — in hex or in `rgb()`,
 * `rgba()`, `hsl()` and `hsla()`, which is what the guard reads — so a palette
 * is changed in one file and the product follows.
 *
 * The narrower claim, that there is no raw *hex*, is what stood here while the
 * guard matched hex alone, and it was true as written and misleading as read:
 * two surfaces were drawing an `rgba()` of their own underneath it.
 *
 * Distance is not there yet, and the docstring used to claim it was. What is
 * left is counted rather than described — `theme.test.ts` holds the sheet to a
 * ceiling on pixel literals that only ever comes down — and each survivor is a
 * value the design system has not ruled on: a card's own padding, the plot's
 * height, the widths of a swatch and a rule. Every distance `DS-Space` names is
 * a token.
 */
const BASE_CSS = `
/* On the document rather than on the page shell: the app bar, the provenance
   banner and every dialog the browser opens outside it inherit from here, and
   before this they inherited the user agent's serif. */
body {
  background: var(--nw-ground);
  color: var(--nw-text);
  font: var(--nw-weight-body) var(--nw-text-body)/var(--nw-text-body-leading) var(--nw-font);
  margin: 0;
  -webkit-font-smoothing: antialiased;
}

/**
 * Tabular figures where there are figures, and nowhere else.
 *
 * Schibsted Grotesk widens its comma, full stop and colon to a digit's width
 * under tabular figures, which is what holds a column of amounts together and
 * what makes a paragraph read as though it had been spaced out by hand. The
 * mockups set it on the body; this product is mostly sentences, so it is set on
 * the figures, and not on a label the plot writes beside a line.
 */
.nw-figure, .nw-chip, .nw-stat-value, .nw table, .nw-data, .nw .nw-num,
.nw-tooltip-value, .nw-callout-value, .nw-tile dd, .nw-plot text:not(.recharts-label) {
  font-feature-settings: 'tnum' 1, 'lnum' 1;
  font-variant-numeric: tabular-nums lining-nums;
}

/* The page max is the frame, padding included — the same 1280 the app bar and
   the tab band are already held to, and the figure the sidebar is derived from.
   Left on the content box the page came out 64 wider than the bar above it, so
   the cards started 32px left of the logo and the sidebar could never land on
   its 356. */
.nw {
  background: var(--nw-ground);
  box-sizing: border-box;
  color: var(--nw-text);
  font: var(--nw-weight-body) var(--nw-text-body)/var(--nw-text-body-leading) var(--nw-font);
  margin: 0 auto;
  max-width: var(--nw-page-max);
  min-height: 100vh;
  padding: var(--nw-page-pad-y) var(--nw-page-pad-x) 64px;
}
.nw h1 {
  font-size: var(--nw-text-page); font-weight: var(--nw-weight-figure);
  letter-spacing: var(--nw-track-page); line-height: 1.1; margin: 0 0 var(--nw-space-2);
}
.nw h2 {
  font-size: var(--nw-text-title); font-weight: var(--nw-weight-strong);
  letter-spacing: var(--nw-track-title); line-height: var(--nw-text-title-leading); margin: 0;
}
.nw .nw-sub { color: var(--nw-muted); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-stack); }
.nw .nw-muted { color: var(--nw-muted); }
.nw a { color: var(--nw-accent); }
.nw a:hover { color: var(--nw-accent-hover); }

/**
 * One focus treatment, everywhere, and nothing to remember.
 *
 * The 2px accent edge and the 4px ring are what the Add-holding mockup draws on
 * its focused field, and they are the system's answer to a product that had no
 * visible focus state at all and so could not be used from a keyboard. Drawn as
 * an outline rather than by thickening a border, so nothing on the page moves
 * when focus arrives.
 *
 * Unscoped, because the version that named its surfaces — the page, the bar, a
 * dialog — shipped the tab band with the browser's own blue hairline on the one
 * row in the product whose unselected items are reachable only by arrow key.
 * Adding the band to the list fixed that band; it did not fix the next one, and
 * a surface that forgets to name itself fails silently and looks fine to
 * everyone with a mouse. This sheet is the whole product's, so there is nothing
 * left outside it to protect.
 */
:focus-visible {
  border-radius: var(--nw-radius-md);
  box-shadow: 0 0 0 var(--nw-focus-ring-w) var(--nw-focus-ring);
  outline: var(--nw-focus-edge) solid var(--nw-focus);
  outline-offset: 0;
}

/**
 * What an unclassed control looks like — a date field and a Retire button sat on
 * the holdings table in whatever the operating system felt like drawing.
 *
 * Named on the modal as well as on the page: a dialog is opened from the app bar
 * and rendered outside the page shell, so a form inside one was reaching none of
 * these.
 *
 * Matched through :where(), which contributes nothing to specificity, so these
 * are genuinely defaults: any component below overrides them by its class alone
 * rather than by out-weighing a two-part element selector, which no single
 * class can.
 */
.nw :where(input, select, textarea), .nw-modal :where(input, select, textarea) {
  background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-md); box-sizing: border-box; color: var(--nw-text);
  font: inherit; font-size: var(--nw-text-body); min-height: var(--nw-h-control-md); padding: 0 14px;
}
.nw :where(textarea), .nw-modal :where(textarea) { padding: 10px 14px; }
.nw :where(input[type='checkbox'], input[type='radio']), .nw-modal :where(input[type='checkbox'], input[type='radio']) { accent-color: var(--nw-accent); min-height: 0; padding: 0; }
.nw :where(button), .nw-modal :where(button) {
  background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-full); color: var(--nw-text); cursor: pointer; font: inherit;
  font-size: var(--nw-text-control); font-weight: var(--nw-weight-strong);
  min-height: var(--nw-h-control-sm); padding: 0 var(--nw-space-4);
}
.nw :where(button:hover:enabled), .nw-modal :where(button:hover:enabled) { background: var(--nw-hover); }
.nw :where(button:disabled), .nw-modal :where(button:disabled) { cursor: not-allowed; opacity: .55; }
.nw :where(button[data-kind='primary'], button[type='submit']), .nw-modal :where(button[data-kind='primary'], button[type='submit']) {
  background: var(--nw-accent); border-color: var(--nw-accent); color: var(--nw-accent-ink);
}
.nw :where(button[data-kind='danger']), .nw-modal :where(button[data-kind='danger']) { border-color: var(--nw-loss-edge); color: var(--nw-loss); }
.nw :where(label), .nw-modal :where(label) { font-size: var(--nw-text-body); }
${CONTROL_SELECTORS.join(', ')} { text-decoration: none; }
/* An icon in a flex control has no intrinsic width to defend. */
.nw button > svg, .nw-appbar button > svg { flex: none; }

/* ── The bar the product sits under ───────────────────────────────────────── */

.nw-appbar { background: var(--nw-card); border-bottom: var(--nw-hairline-width) solid var(--nw-hairline); }
.nw-appbar-inner {
  align-items: center; box-sizing: border-box; display: flex; gap: var(--nw-space-8);
  margin: 0 auto; max-width: var(--nw-page-max); min-height: var(--nw-h-nav-bar);
  padding: 0 var(--nw-page-pad-x);
}
.nw-mark {
  align-items: center; color: var(--nw-text); display: flex; flex: none; gap: var(--nw-space-3);
  font: var(--nw-weight-figure) var(--nw-text-wordmark)/1.2 var(--nw-font);
  letter-spacing: var(--nw-track-wordmark);
}
.nw-appbar-go { align-items: center; display: flex; flex: 1; gap: var(--nw-space-8); min-width: 0; }
.nw-appbar-lead { align-items: center; display: flex; flex: none; gap: var(--nw-space-3); margin-left: auto; }
.nw-appbar-end { align-items: center; display: flex; flex: none; gap: var(--nw-space-3); margin-left: var(--nw-space-6); }
.nw-add-mark { display: none; }
.nw-updated { color: var(--nw-muted); font-size: var(--nw-text-cell); white-space: nowrap; }

/* Four destinations fit at every width this is read at, but the row still
   scrolls rather than wrapping the bar into two, because a household can be
   named anything and the mark and the avatar have nowhere to go. */
.nw-pills { display: flex; gap: var(--nw-space-1); min-width: 0; overflow-x: auto; scrollbar-width: none; }
.nw-pills::-webkit-scrollbar { display: none; }
.nw-pills a {
  border-radius: var(--nw-radius-full); color: var(--nw-muted); font-size: var(--nw-text-body);
  font-weight: var(--nw-weight-medium); padding: var(--nw-space-2) 14px; text-decoration: none;
  white-space: nowrap;
}
.nw-pills a:hover { background: var(--nw-hover); color: var(--nw-text); }
.nw-pills a[aria-current='page'] { background: var(--nw-selected); color: var(--nw-text); }

.nw-avatar {
  align-items: center; background: var(--nw-avatar); border-radius: var(--nw-radius-full);
  color: var(--nw-avatar-ink); display: flex; flex: none; font-size: var(--nw-text-cell);
  font-weight: var(--nw-weight-strong); height: var(--nw-h-avatar-md); justify-content: center;
  width: var(--nw-h-avatar-md);
}

/* ── Cards ────────────────────────────────────────────────────────────────── */

.nw-card {
  background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-lg); box-shadow: var(--nw-shadow-card); box-sizing: border-box;
  padding: var(--nw-card-pad-y) var(--nw-card-pad-x);
}
/* A card laid straight on the page carries the gap to whatever is under it.
   Cards inside a stack or a column take their gap from the stack instead, so
   only the direct children of the page need this. */
.nw > .nw-card { margin-bottom: var(--nw-card-gap); }
.nw-card[data-pad='tight'] { padding: var(--nw-space-5) 22px; }
/**
 * A card that pads its own rows instead, and the inset it pads them by.
 *
 * Held as a custom property because two things need the same number and one of
 * them is a table cell: the head reads it here and the outer cells read it from
 * the table rule below, so a flush card cannot again give its inset to the
 * heading alone. It did, and the effect was a heading starting 26px inside a
 * card whose table started at the border — the column headings, the monograms
 * and the buttons at the end of every row all hanging outside the one column
 * the card otherwise reads down.
 */
.nw-card[data-pad='flush'] { --nw-table-inset: var(--nw-card-pad-x); padding: var(--nw-space-5) 0 0; }
.nw-card[data-pad='flush'] > .nw-card-head, .nw-card[data-pad='flush'] > .nw-card-foot { padding: 0 var(--nw-table-inset); }
.nw-card-head { align-items: baseline; display: flex; flex-wrap: wrap; gap: var(--nw-space-2) var(--nw-space-4); justify-content: space-between; }
.nw-card-aside { align-items: baseline; color: var(--nw-muted); display: flex; flex-wrap: wrap; font-size: var(--nw-text-chip); gap: var(--nw-space-2) var(--nw-space-4); }
.nw-card-title { min-width: 0; }
.nw-card-lede { color: var(--nw-muted); font-size: var(--nw-text-chip); line-height: 1.5; margin: 3px 0 0; max-width: 88ch; }
.nw-card-foot { color: var(--nw-faint); font-size: var(--nw-text-meta); padding-top: var(--nw-space-3); }

.nw-stat { padding: 18px var(--nw-space-5); }
.nw-stat-label { align-items: center; color: var(--nw-muted); display: flex; font-size: var(--nw-text-cell); font-weight: var(--nw-weight-medium); gap: 7px; }
/* The minimum height of an unclassed control is taken back off, this being a
   button and so one of them: the dot is drawn as a circle of 15px and had been
   standing up as a 38px capsule beside every figure it explains. */
.nw-help {
  align-items: center; background: none; border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-full); color: var(--nw-faint); cursor: help; display: flex;
  /* Fitted to the circle rather than taken off the type scale: the glyph has to
     sit inside 15px with a hairline round it, and the nearest step would crowd
     it. Deliberately outside the scale, so a sweep of the literals leaves it. */
  font: var(--nw-weight-strong) 10px/1 var(--nw-font); height: 15px; justify-content: center;
  min-height: 0; padding: 0; width: 15px;
}
/* The same dot, opening something rather than hovering it: it is pressed, so it
   takes the pointer a control takes, and says while it is open that it is. */
.nw-why { cursor: pointer; flex: none; }
.nw-why:hover, .nw-why:focus-visible { border-color: var(--nw-muted); color: var(--nw-muted); }
.nw-why[aria-expanded='true'] { background: var(--nw-ink); border-color: var(--nw-ink); color: var(--nw-card); }
.nw-stat-value {
  font-size: var(--nw-text-stat); font-weight: var(--nw-weight-figure);
  letter-spacing: var(--nw-track-page); line-height: 1; margin-top: 9px;
}
.nw-stat-note { color: var(--nw-muted); font-size: var(--nw-text-chip); margin-top: 9px; }

/* ── Figures ──────────────────────────────────────────────────────────────── */

/**
 * A sum in two parts. The øre steps to half the krone beside it and takes the
 * muted ink; in a cell it does not, because half of 13px is six and an øre
 * nobody can read is a missing digit rather than a hierarchy. The unit keeps a
 * slot of its own everywhere, which is what holds a column of three currencies
 * to one edge.
 */
.nw-figure { white-space: nowrap; }
.nw-figure-minor { color: var(--nw-muted); font-size: var(--nw-text-ore); font-weight: var(--nw-weight-medium); letter-spacing: var(--nw-track-title); }
.nw-figure[data-size='display'] { font-size: var(--nw-text-display); font-weight: var(--nw-weight-figure); letter-spacing: var(--nw-track-display); line-height: var(--nw-text-display-leading); }
.nw-figure[data-size='figure'] { font-size: var(--nw-text-figure); font-weight: var(--nw-weight-figure); letter-spacing: var(--nw-track-figure); line-height: var(--nw-text-figure-leading); }
.nw-figure[data-size='body'] { font-size: var(--nw-text-title); font-weight: var(--nw-weight-strong); }
/* Only the two sizes above the body line carry a leading token: at 46 and 24
   they outgrow the 21px line inherited from the page, and without one they would
   be the bug PRO-243 found. A cell's 13 sits inside that line, so it inherits
   the leading exactly as the body size's 15 does. */
.nw-figure[data-size='cell'] { font-size: var(--nw-text-cell); }
.nw-figure[data-size='body'] .nw-figure-minor, .nw-figure[data-size='cell'] .nw-figure-minor { font-size: 1em; }
.nw td .nw-figure-unit, .nw th .nw-figure-unit { display: inline-block; text-align: left; width: 3.2ch; }

/* A chip is 24 and says so, rather than adding up to whatever its text and its
   padding happen to make. It had been 22 and 27,59 for exactly that reason: the
   line height is the chip's own, not the body's, and once that was fixed the
   padding was still doing the sizing.

   A minimum rather than a height, because the one chip that is a card's whole
   figure is drawn at the figure's size and is taller on purpose.

   One type size. There had been a small and a medium at the same height, the
   same padding and one pixel of type apart, which is not a variant. The axis
   that earns its keep is the role below, and a chip's role is read off where it
   sits rather than asked for. */
.nw-chip {
  align-items: center; border: var(--nw-hairline-width) solid transparent;
  border-radius: var(--nw-radius-full); box-sizing: border-box; display: inline-flex;
  font-size: var(--nw-text-cell); font-weight: var(--nw-weight-strong); gap: 6px;
  line-height: 1.2; min-height: var(--nw-h-chip); padding: 0 var(--nw-chip-pad-x);
  white-space: nowrap;
}
/* Fill against outline is the signal; the colour only confirms it. */
.nw-chip[data-direction='up'] { background: var(--nw-gain-wash); color: var(--nw-gain); }
.nw-chip[data-direction='down'] { background: none; border-color: var(--nw-loss-edge); color: var(--nw-loss); }
/* No chip at all, and the padding kept so the column does not step. Cash and
   property sit at zero most days; a chip on every one of them is noise the two
   that moved would have to compete with. */
.nw-chip[data-direction='flat'] { background: none; border-color: transparent; color: var(--nw-muted); font-weight: var(--nw-weight-body); }
/* Where a card's whole figure is a delta, the chip is the figure and takes the
   figure's size. A 13px pill under a 13px label is not a headline. Written
   after the sizes above, which it has to beat.

   Its height comes from the token for the same reason the 24 above does: a 36
   that falls out of five pixels on 24px type is a height that moves the next
   time the type does, and nothing would notice. The 10 the chip beside text is
   padded by stays in proportion at 14, which is the only distance the padding
   still sets here. */
.nw-stat-value > .nw-chip, .nw-stat-value > .nw-measure > .nw-chip {
  font-size: var(--nw-text-figure); gap: var(--nw-space-2); letter-spacing: var(--nw-track-figure);
  line-height: 1; min-height: var(--nw-h-chip-figure); padding: 0 14px;
}
.nw-stat-value > .nw-chip svg, .nw-stat-value > .nw-measure > .nw-chip svg { height: 14px; width: 14px; }

/* ── Controls ─────────────────────────────────────────────────────────────── */

/* The underline is taken off because half of these are links: Sources draws
   "Value them" and "Open" as buttons on an anchor, and an anchor inside the
   page keeps the underline a button has never had. */
.nw-btn {
  align-items: center; background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-full); color: var(--nw-text); cursor: pointer; display: inline-flex;
  font: var(--nw-weight-strong) var(--nw-text-control)/1 var(--nw-font); gap: var(--nw-space-2);
  min-height: var(--nw-h-control-sm); padding: 0 var(--nw-space-4); text-decoration: none; white-space: nowrap;
}
.nw-btn:hover:enabled { background: var(--nw-hover); }
.nw-btn:disabled { cursor: not-allowed; opacity: .55; }
.nw-btn[data-kind='primary'] {
  background: var(--nw-accent); border-color: var(--nw-accent); color: var(--nw-accent-ink);
  font-size: var(--nw-text-body); min-height: var(--nw-h-control-md); padding: 0 18px;
}
.nw-btn[data-kind='primary']:hover:enabled { background: var(--nw-accent-hover); border-color: var(--nw-accent-hover); }
.nw-btn[data-kind='danger'] { border-color: var(--nw-loss-edge); color: var(--nw-loss); }

/* Its own edge rather than the hairline. This is the way in from an empty
   Overview, and drawn at the faintest line the system owns it read as a
   disabled field; the mockup gives it a dashed edge two steps darker. */
.nw-dashed {
  background: none; border: var(--nw-hairline-width) dashed var(--nw-dashed-edge); border-radius: var(--nw-radius-md);
  color: var(--nw-accent); cursor: pointer; font: var(--nw-weight-strong) var(--nw-text-cell)/1 var(--nw-font);
  min-height: var(--nw-tap-min); text-decoration: none; width: 100%;
}
.nw-dashed:hover:enabled { background: var(--nw-hover); }

/**
 * Two heights, and they were never the same question.
 *
 * The item is 38 — what it has always measured, and what the system asks for,
 * and it is arithmetic on the track rather than a step on the height scale, so
 * no free-standing control can reach it by reading the same name.
 * The target is 44, which is what was missing: the control was a size nobody had
 * chosen only because nobody had noticed there were two numbers to choose. The
 * trough's own 3px of padding is the six pixels the target is short, so the
 * pseudo-element takes exactly the strip of trough above and below each item
 * that was reachable by eye and not by thumb. It draws nothing.
 */
.nw-segmented { background: var(--nw-ground); border-radius: var(--nw-radius-full); display: flex; padding: var(--nw-segmented-inset); }
.nw-segmented button {
  background: none; border: 0; border-radius: var(--nw-radius-full); color: var(--nw-muted);
  cursor: pointer; font: var(--nw-weight-medium) var(--nw-text-cell)/1 var(--nw-font);
  min-height: var(--nw-h-segmented-item); padding: 7px 14px; position: relative; white-space: nowrap;
}
.nw-segmented button::before {
  content: ''; inset: calc((var(--nw-h-segmented-item) - var(--nw-tap-min)) / 2) 0; position: absolute;
}
.nw-segmented button:hover { color: var(--nw-text); }
.nw-segmented button[aria-pressed='true'] {
  background: var(--nw-card); box-shadow: var(--nw-shadow-pop); color: var(--nw-text); font-weight: var(--nw-weight-strong);
}
/* The one place in the sheet where a component's own shadow meets the focus
   ring, and the selected item was winning: it kept the 2px edge and lost the
   4px ring, which is only visible to somebody arriving by Tab. Both, in order,
   rather than one rule quietly cancelling the other. */
.nw-segmented button[aria-pressed='true']:focus-visible {
  box-shadow: var(--nw-shadow-pop), 0 0 0 var(--nw-focus-ring-w) var(--nw-focus-ring);
}

/* The band the destination's tabs sit in, between the bar and the page. The
   selected tab's 2px rule lands on the band's own hairline, so the two read as
   one edge rather than as a double line. */
.nw-subnav { background: var(--nw-card); border-bottom: var(--nw-hairline-width) solid var(--nw-hairline); }
.nw-subnav-inner {
  box-sizing: border-box; margin: 0 auto; max-width: var(--nw-page-max);
  overflow-x: auto; padding: var(--nw-space-4) var(--nw-page-pad-x) 0; scrollbar-width: none;
}
.nw-subnav-inner::-webkit-scrollbar { display: none; }

/* 44, from the tab-strip token, and the leading left alone. The band had been
   29 because this rule set \`line-height: 1\` where nothing asked it to — the same
   cause as the chip above, on the one row whose unselected items are reachable
   only by arrow key. */
.nw-tabs { display: flex; gap: 26px; }
.nw-tabs button {
  align-items: center; background: none; border: 0; border-bottom: 2px solid transparent;
  border-radius: 0; color: var(--nw-muted); cursor: pointer; display: inline-flex; flex: none;
  font: var(--nw-weight-medium) var(--nw-text-body)/var(--nw-text-body-leading) var(--nw-font);
  min-height: var(--nw-h-tab-strip); padding: 0; white-space: nowrap;
}
.nw-tabs button:hover { color: var(--nw-text); }
.nw-tabs button[aria-selected='true'] { border-bottom-color: var(--nw-accent); color: var(--nw-text); font-weight: var(--nw-weight-strong); }

/* ── Standing ─────────────────────────────────────────────────────────────── */

/* Sized the way the delta chip is sized, because a reader of a page cannot tell
   the two apart and was getting 29 from one and 24 from the other. The height is
   the token rather than whatever the text and its padding happened to add up
   to, which is what let the two drift while both looked deliberate. */
.nw-status {
  align-items: center; border: var(--nw-hairline-width) solid transparent;
  border-radius: var(--nw-radius-full); box-sizing: border-box; display: inline-flex;
  font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); gap: 4px;
  line-height: 1.2; min-height: var(--nw-h-chip); padding: 0 var(--nw-chip-pad-x);
  white-space: nowrap;
}
/* currentColor, not a colour of its own: the glyph rides the ink the tone
   already set, so it reinforces the word rather than adding a second signal. */
.nw-status svg { flex: none; }
.nw-status[data-tone='live'] { background: var(--nw-gain-wash); color: var(--nw-gain); }
.nw-status[data-tone='caution'] { background: var(--nw-warning-wash); color: var(--nw-warning); }
.nw-status[data-tone='failed'] { background: var(--nw-card); border-color: var(--nw-loss-edge); color: var(--nw-loss); }
/* Neither good nor bad news: a holding nobody can sell is a fact about it. */
.nw-status[data-tone='manual'] { background: var(--nw-hover); color: var(--nw-muted); }

.nw-source { align-items: center; border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule); display: flex; gap: var(--nw-space-3); padding: 11px 0; }
/* The tile takes the colour of what it stands for. It is read straight down the
   left edge of a long list, which is the one place on the page where colour
   sorts faster than a word does. */
.nw-monogram {
  align-items: center; background: var(--nw-hover); border-radius: var(--nw-radius-sm); color: var(--nw-muted);
  display: flex; flex: none; font-size: var(--nw-text-meta); font-weight: var(--nw-weight-figure);
  height: var(--nw-h-avatar-md); justify-content: center; width: var(--nw-h-avatar-md);
}
/* The small tile keeps the same corner as the large one: they are the same tile
   read at two sizes, and the 8 it carried made the inline one read rounder. */
.nw-monogram[data-size='sm'] { font-size: var(--nw-text-meta); height: var(--nw-h-avatar-sm); width: var(--nw-h-avatar-sm); }
.nw-monogram[data-tone='property'] { background: var(--nw-tone-property); color: var(--nw-tone-property-ink); }
.nw-monogram[data-tone='equity'] { background: var(--nw-tone-equity); color: var(--nw-tone-equity-ink); }
.nw-monogram[data-tone='crypto'] { background: var(--nw-tone-crypto); color: var(--nw-tone-crypto-ink); }
.nw-monogram[data-tone='pension'] { background: var(--nw-tone-pension); color: var(--nw-tone-pension-ink); }
.nw-monogram[data-tone='cash'] { background: var(--nw-tone-cash); color: var(--nw-tone-cash-ink); }
.nw-monogram[data-tone='liability'] { background: var(--nw-tone-liability); color: var(--nw-tone-liability-ink); }
.nw-source-name { flex: 1 1 auto; min-width: 0; }
.nw-source-name > span { display: block; font-size: var(--nw-text-cell); font-weight: var(--nw-weight-medium); overflow-wrap: anywhere; }
.nw-source-name small { color: var(--nw-muted); display: block; font-size: var(--nw-text-meta); }

.nw-notices { list-style: none; margin: 0; padding: 0; }
.nw-notices li { border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule); display: flex; font-size: var(--nw-text-cell); gap: 11px; line-height: 1.45; padding: 10px 0; }
.nw-notices li:last-child { border-bottom: 0; }
.nw-notice-dot { border-radius: var(--nw-radius-full); flex: none; height: 8px; margin-top: 5px; width: 8px; }
.nw-notices li[data-tone='warning'] .nw-notice-dot { background: var(--nw-warning); }
.nw-notices li[data-tone='critical'] .nw-notice-dot { background: var(--nw-critical); }
/* Muted, the colour the plot marks a stretch with fewer sources in: that is not
   a fault in the figures it holds, and under the warning dot it read as one. */
.nw-notices li[data-tone='neutral'] .nw-notice-dot { background: var(--nw-muted); }

/* ── Tables ───────────────────────────────────────────────────────────────── */

.nw-data { border-collapse: collapse; font-size: var(--nw-text-body); width: 100%; }
/* The column headings, and only those. A row heading is a cell that happens to
   be a heading; set uppercase and faint, it read as a second header band running
   down the left of every table that has one. */
.nw-data thead th {
  background: none; border: 0; color: var(--nw-faint); font-size: var(--nw-text-meta);
  font-weight: var(--nw-weight-strong); height: var(--nw-h-table-header); letter-spacing: var(--nw-track-label);
  padding: 0; text-align: left; text-transform: uppercase; vertical-align: middle;
}
/* 44, which is what the foundations mean by a table row and what Holdings is
   drawn at. \`--nw-h-table-row\` had been declared for this and read by nothing,
   so every table in the product sat three pixels under its own design. */
.nw-data td, .nw-data tbody th {
  border: 0; border-top: var(--nw-hairline-width) solid var(--nw-row-rule);
  font-size: var(--nw-text-body); height: var(--nw-h-table-row); padding: 0; vertical-align: middle;
}
.nw-data tbody th { font-weight: var(--nw-weight-medium); text-align: left; }
/* A dense row is the system's compact row, which is 36 and not the 31 that had
   been typed here. A row height is the value that went wrong once already, and
   it is the only kind of number in this sheet that no page can be trusted to
   pick for itself. */
.nw-data[data-dense='true'] td, .nw-data[data-dense='true'] tbody th { font-size: var(--nw-text-cell); height: var(--nw-h-table-row-compact); }
.nw-data .nw-num { text-align: right; }
/* Air between columns. A right-aligned figure and the heading of the column
   after it otherwise meet with nothing in between, which is how VALUE and
   FLAGS came to be one word. */
.nw-data :where(th, td):not(:last-child) { padding-right: var(--nw-space-4); }
/* The inset a table's outer cells take, given to the cells at the two ends
   rather than to the table: a rule between two rows still runs the full width of
   the card, and what is written in the row starts where the card's heading
   starts.

   Zero by default and set by whatever contains the table, which is the point.
   Holdings wanted a column that started a little further in and had to write its
   own \`:first-child\` rule to get one, because the system offered no way to ask
   — and a page writing that rule is how the flush card came to give its inset to
   the heading and not to the cells. */
.nw-data :where(th, td):first-child { padding-left: var(--nw-table-inset, 0); }
.nw-data :where(th, td):last-child { padding-right: var(--nw-table-inset-end, var(--nw-table-inset, 0)); }
/* A name and what reports it, on two lines that still fit the row. Clipped
   rather than wrapped: a fund with a long name would otherwise set the height of
   every row around it. */
.nw-data .nw-source-name > span, .nw-data .nw-source-name small {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.nw-data .nw-source-name > span { line-height: var(--nw-text-cell-leading); }
.nw-data .nw-source-name small { line-height: var(--nw-text-meta-leading); }
.nw-data tbody tr:hover td { background: var(--nw-hover); }
/* What the table adds up to, under a rule heavier than the ones between rows. */
.nw-data tfoot td {
  border-top: var(--nw-hairline-width) solid var(--nw-hairline); font-weight: var(--nw-weight-strong);
  height: var(--nw-h-table-total);
}
.nw-swatch { border-radius: 3px; display: inline-block; flex: none; height: 10px; width: 10px; }
/* A swatch, a monogram or a chip, and the thing it stands beside. */
.nw-measure { align-items: center; display: flex; gap: var(--nw-space-2); }
/* Whatever a card's head is followed by, it is not followed closely. */
.nw-card > .nw-card-head { margin-bottom: var(--nw-space-4); }

/* One share of a whole, against the whole. The stacked bar is every share at
   once; this is the one a row is about. */
.nw-bar { background: var(--nw-row-rule); border-radius: 3px; display: block; height: 6px; overflow: hidden; width: 100%; }
.nw-bar i { border-radius: 3px; display: block; height: 100%; }
.nw-share { align-items: center; display: flex; gap: 10px; }
/* The share never wraps: the bar beside it gives way first, and a hundred per
   cent is one character longer than every other row. */
.nw-share b { font-weight: var(--nw-weight-strong); min-width: 56px; text-align: right; white-space: nowrap; }
.nw-stacked { border-radius: var(--nw-radius-full); display: grid; height: var(--nw-bar-h); overflow: hidden; }
.nw-stacked > span { display: block; height: 100%; }

/* ── The chart, and the card it lives in ──────────────────────────────────── */

.nw-plot-panel { background: var(--nw-inner); border: var(--nw-hairline-width) solid var(--nw-row-rule); border-radius: var(--nw-radius-panel); padding: var(--nw-space-4); }
.nw-plot-head { align-items: baseline; display: flex; font-size: var(--nw-text-cell); font-weight: var(--nw-weight-strong); justify-content: space-between; margin-bottom: var(--nw-space-3); }
.nw-plot-head > :last-child { color: var(--nw-faint); font-size: var(--nw-text-meta); font-weight: var(--nw-weight-body); }
.nw-plot { height: var(--nw-plot-height); position: relative; width: 100%; }
.nw-plot--loading { opacity: .45; transition: opacity .15s; }
/* The plot's weights are set from CSS rather than from props: SVG presentation
   attributes lose to a stylesheet, and that is what keeps the line's weight a
   token like every other value here. */
.nw-plot .recharts-line-curve { stroke-linecap: var(--nw-chart-cap); stroke-linejoin: var(--nw-chart-cap); stroke-width: var(--nw-chart-line); }
.nw-plot .recharts-cartesian-grid line { stroke: var(--nw-row-rule); stroke-width: var(--nw-chart-grid-width); }
.nw-plot .recharts-line-dots circle { r: calc(var(--nw-chart-dot) / 2); }
/* The wash under the first series, from the same stylesheet the line's weight
   comes from: a presentation attribute cannot hold a custom property, and the
   two themes need two opacities for the wash to read on either ground. */
.nw-plot .recharts-area-area { fill: var(--nw-accent); fill-opacity: var(--nw-chart-fill); }
.nw-plot .recharts-cartesian-axis-tick text { fill: var(--nw-faint); font-size: var(--nw-text-meta); }

.nw-plot-inner { height: 100%; position: relative; width: 100%; }
.nw-callout {
  background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-sm); box-shadow: var(--nw-shadow-pop); padding: 7px 11px;
}
.nw-callout-label { color: var(--nw-muted); display: block; font-size: var(--nw-text-meta); }
.nw-callout-value { display: block; font-size: var(--nw-text-cell); font-weight: var(--nw-weight-figure); margin-top: 2px; }
/* Placed on the gap it names, halfway between the two lines at their newest
   reading, which is why the plot is given a domain rather than left to pick one.
   Held off the right-hand end, where the hatching that says a figure was carried
   forward would be underneath it. */
.nw-callout--gap {
  left: 40%; max-width: 210px; pointer-events: none; position: absolute;
  transform: translate(-50%, -50%); z-index: 2;
}

/* The legend is a control: each key is drawn in its line's own dash pattern, and
   a key that is off dims without moving. */
.nw-series { display: flex; flex-wrap: wrap; gap: var(--nw-space-2) 18px; margin: var(--nw-space-3) 0 var(--nw-space-1); }
.nw-series button {
  align-items: center; background: none; border: 0; border-radius: var(--nw-radius-full);
  color: var(--nw-body-ink); cursor: pointer; display: flex;
  font: var(--nw-weight-medium) var(--nw-text-chip)/1.4 var(--nw-font); gap: 7px; min-height: auto; padding: 2px 0;
}
.nw-series button[aria-pressed='false'] { color: var(--nw-faint); }
.nw-series button[aria-pressed='false'] .nw-series-key { opacity: .4; }
.nw-series-key { display: block; flex: none; height: 0; width: 18px; }
.nw-series-key[data-stroke='solid'] { border-top: 2.5px solid currentColor; }
.nw-series-key[data-stroke='dashed'] { border-top: 2px dashed currentColor; }
.nw-series-key[data-stroke='dotted'] { border-top: 2px dotted currentColor; }
.nw-series-extra { align-items: center; color: var(--nw-body-ink); display: flex; font-size: var(--nw-text-chip); gap: 7px; }

.nw-legend { display: flex; flex-wrap: wrap; gap: var(--nw-space-2) 18px; list-style: none; margin: var(--nw-space-3) 0 var(--nw-space-1); padding: 0; }
.nw-legend li { align-items: center; color: var(--nw-body-ink); display: flex; font-size: var(--nw-text-chip); gap: 7px; }
/* A key draws its shading the way the plot does. The plot's 45-degree hatch is
   135deg here, because CSS angles a gradient across its stripes where SVG turns
   the line itself: at 45deg every key leaned the way carried forward does, the
   modelled one included. The other lean is the same stripes mirrored, which
   costs no distance, and flat is the plot's own wash of the colour. */
.nw-hatch-key { background: repeating-linear-gradient(135deg, transparent, transparent 2px, currentColor 2px, currentColor 3px); border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: 2px; flex: none; height: 11px; width: 11px; }
.nw-hatch-key[data-texture='falling'] { transform: scaleX(-1); }
.nw-hatch-key[data-texture='flat'] { background: color-mix(in srgb, currentColor ${COVERAGE_WASH_PERCENT}%, transparent); }
.nw-key { border-radius: 3px; display: inline-block; height: 10px; width: 10px; }
.nw-key--line { border-radius: var(--nw-radius-full); height: 3px; width: 16px; }

/* ── The modal, to the pixel ──────────────────────────────────────────────── */

.nw-modal {
  background: var(--nw-card); border: 0; border-radius: var(--nw-radius-xl);
  box-shadow: var(--nw-shadow-dialog); box-sizing: border-box; color: var(--nw-text);
  max-height: calc(100vh - var(--nw-space-12)); max-width: var(--nw-modal-w); padding: 0;
  width: calc(100vw - var(--nw-space-8));
}
.nw-modal::backdrop { background: var(--nw-scrim); }
/* The two-column dialog: a guide to somebody else's interface beside the box it
   ends in, which at 576 is a column four words wide. Scoped to \`[open]\`,
   because a dialog is hidden by the browser's own \`display: none\` and a rule
   setting \`display\` unconditionally would leave every shut one on the page. */
.nw-modal[data-width='wide'] { max-width: var(--nw-modal-wide-w); }
.nw-modal[data-width='wide'][open] { display: flex; flex-direction: column; }
.nw-modal[data-width='wide'] .nw-modal-body { min-height: 0; overflow-y: auto; }
.nw-modal-head { align-items: flex-start; display: flex; gap: var(--nw-space-4); justify-content: space-between; padding: var(--nw-space-7) var(--nw-space-8) 0; }
/* The leading comes with the size, and the lede below is the reason: it asks
   for 6px of clearance, and against a line box shorter than its own type the
   title's descenders eat most of it. The gap the sheet specifies is not the gap
   anybody has been seeing. */
.nw-modal-head h2 {
  font-size: var(--nw-text-figure); font-weight: var(--nw-weight-figure);
  letter-spacing: var(--nw-track-figure); line-height: var(--nw-text-figure-leading); margin: 0;
}
.nw-modal-head p { color: var(--nw-muted); font-size: var(--nw-text-cell); line-height: 1.5; margin: 6px 0 0; max-width: 400px; }
.nw-modal-close {
  align-items: center; background: var(--nw-ground); border: 0; border-radius: var(--nw-radius-full);
  color: var(--nw-muted); cursor: pointer; display: flex; flex: none;
  height: var(--nw-h-icon-btn); justify-content: center; min-height: auto; padding: 0;
  width: var(--nw-h-icon-btn);
}
.nw-modal-body { display: flex; flex-direction: column; gap: 18px; padding: var(--nw-space-6) var(--nw-space-8) var(--nw-space-1); }
.nw-modal-foot { display: flex; gap: var(--nw-space-3); justify-content: flex-end; padding: 22px var(--nw-space-8) 26px; }
.nw-modal-foot .nw-btn { min-height: var(--nw-footer-btn-h); padding: 0 22px; }
/* The way out of a dialog is a filled grey pill, not an outlined white one: on
   a white dialog a card-coloured button with a hairline round it is a rectangle
   of nothing beside the primary, which is what C-AddHolding draws it against. */
.nw-modal-foot .nw-btn[data-kind='secondary'] { background: var(--nw-ground); border-color: transparent; }
.nw-modal-foot .nw-btn[data-kind='secondary']:hover:enabled { background: var(--nw-selected); }
.nw-modal-foot .nw-btn[data-kind='primary'] { padding: 0 26px; }

.nw-field { display: flex; flex-direction: column; gap: 7px; }
.nw-field[data-width='narrow'] { width: 118px; }
.nw-field[data-width='set'] { flex: 1 1 auto; }
.nw-field > label { color: var(--nw-text); font-size: var(--nw-text-cell); font-weight: var(--nw-weight-medium); }
.nw-field input, .nw-field select {
  background: var(--nw-ground); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-field-radius); box-sizing: border-box; color: var(--nw-text);
  font: inherit; font-size: var(--nw-text-field); min-height: var(--nw-field-h); padding: 0 14px; width: 100%;
}
.nw-fields { display: flex; gap: 14px; }
.nw-unit-field {
  align-items: center; background: var(--nw-ground); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-field-radius); box-sizing: border-box; display: flex; height: var(--nw-field-h); padding: 0 14px;
}
.nw-unit-field input { background: none; border: 0; flex: 1 1 auto; min-height: auto; padding: 0; text-align: right; }
.nw-unit-field input:focus-visible { box-shadow: none; outline: 0; }
/* The ring belongs to the field, not to the box inside it. */
.nw-unit-field:focus-within { border-color: var(--nw-focus); border-width: var(--nw-focus-edge); box-shadow: 0 0 0 var(--nw-focus-ring-w) var(--nw-focus-ring); padding: 0 13px; }
.nw-unit-field > span { color: var(--nw-muted); font-size: var(--nw-text-body); margin-left: var(--nw-space-2); }

.nw-preview {
  align-items: center; background: var(--nw-preview); border-radius: var(--nw-radius-panel);
  display: flex; flex-wrap: wrap; gap: var(--nw-space-3); justify-content: space-between; padding: var(--nw-space-4) 18px;
}
.nw-preview-label { color: var(--nw-text); display: block; font-size: var(--nw-text-cell); font-weight: var(--nw-weight-medium); }
.nw-preview-caveat { color: var(--nw-preview-ink); display: block; font-size: var(--nw-text-chip); margin-top: 3px; }
.nw-preview-value { align-items: center; display: flex; gap: var(--nw-space-3); }
.nw-check { align-items: center; display: flex; gap: 11px; }
.nw-check input { flex: none; height: 18px; width: 18px; }

.nw-tooltip {
  background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-sm); box-shadow: var(--nw-shadow-pop); font-size: var(--nw-text-cell);
  min-width: 190px; padding: 9px 11px;
}
.nw-tooltip h3 { color: var(--nw-muted); font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); margin: 0 0 var(--nw-space-2); }
.nw-tooltip dl { display: grid; gap: 2px 10px; grid-template-columns: auto 1fr auto; margin: 0; }
.nw-tooltip dl > div { display: contents; }
.nw-tooltip dt { align-items: center; display: flex; margin: 0; }
.nw-tooltip dd { margin: 0; }
.nw-tooltip .nw-tooltip-name { color: var(--nw-muted); }
.nw-tooltip .nw-tooltip-value { color: var(--nw-text); font-weight: var(--nw-weight-strong); text-align: right; }
.nw-tooltip .nw-tooltip-flags { border-top: var(--nw-hairline-width) solid var(--nw-hairline); color: var(--nw-muted); margin: 7px 0 0; padding-top: var(--nw-space-2); }
.nw-tooltip .nw-tooltip-coverage { color: var(--nw-muted); margin: 0 0 var(--nw-space-2); }

/* ── The pages still on the tokens alone ──────────────────────────────────── */

.nw-page-head { align-items: flex-start; display: flex; flex-wrap: wrap; gap: var(--nw-space-4); justify-content: space-between; margin-bottom: var(--nw-stack); }
.nw-cols { display: flex; flex-wrap: wrap; gap: var(--nw-card-gap); }
.nw-cols > .nw-col-main { flex: 1 1 620px; min-width: 0; }
.nw-cols > .nw-col-side { display: flex; flex: 1 1 var(--nw-sidebar-w); flex-direction: column; gap: var(--nw-card-gap); min-width: 0; }
/* The sidebar is held at its width only while a main column is beside it to
   take the other 840. Once the two wrap, the one below has a line to itself and
   has to fill it, which a basis that never grows would leave 356 of. */
@media (min-width: 1060px) {
  .nw-cols > .nw-col-side { flex-grow: 0; }
}
.nw-stack { display: flex; flex-direction: column; gap: var(--nw-card-gap); }

.nw-filters { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-3) var(--nw-space-6); margin-bottom: var(--nw-stack); }
.nw-filter { align-items: center; display: flex; gap: var(--nw-space-2); }
.nw-filter > span { color: var(--nw-faint); font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); text-transform: uppercase; }


.nw-note { color: var(--nw-muted); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-stack); max-width: 78ch; }
/* A footnote that ran the full width of the page read as an alarm, and four of
   them on one page read as an outage. Held to the measure of what they qualify. */
.nw-flag {
  align-items: flex-start; border: var(--nw-hairline-width) solid transparent;
  border-radius: var(--nw-radius-md); display: flex; font-size: var(--nw-text-cell);
  gap: var(--nw-space-2); margin: 0 0 var(--nw-space-3); max-width: 96ch; padding: 9px var(--nw-space-3);
}
.nw-flag--modelled { background: var(--nw-warning-wash); border-color: var(--nw-warning-wash); }
.nw-flag--stale { background: color-mix(in srgb, var(--nw-loss) 10%, transparent); border-color: var(--nw-loss-edge); }
.nw-flag--missing { background: color-mix(in srgb, var(--nw-critical) 10%, transparent); border-color: var(--nw-loss-edge); }
/* The one flag that is good news. It arrived on the page that first had any,
   and the tones belong together or the next page to need one writes its own. */
.nw-flag--granted { background: color-mix(in srgb, var(--nw-gain) 12%, transparent); border-color: color-mix(in srgb, var(--nw-gain) 40%, transparent); }
.nw-flag b { font-weight: var(--nw-weight-strong); }
.nw-flag-icon { flex: none; font-size: var(--nw-text-body); line-height: 1.35; }

.nw-table-wrap { max-height: 360px; overflow: auto; }
.nw .nw-num { text-align: right; white-space: nowrap; }
.nw tbody tr[data-flagged='true'] td:first-child { box-shadow: inset 3px 0 0 var(--nw-warning); }

.nw-missing { color: var(--nw-critical); }


.nw-empty { color: var(--nw-muted); padding: var(--nw-space-12) 0; text-align: center; }
.nw-sr-only { clip: rect(0 0 0 0); clip-path: inset(50%); height: 1px; overflow: hidden; position: absolute; white-space: nowrap; width: 1px; }

.nw-pill-rule { display: none; }

/* ── The account, behind the avatar ───────────────────────────────────────── */

.nw-menu { position: relative; }
.nw-menu-trigger {
  background: none; border: 0; border-radius: var(--nw-radius-full); cursor: pointer;
  display: block; font: inherit; min-height: auto; padding: 0;
}
.nw-menu-panel {
  background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-panel); box-shadow: var(--nw-shadow-menu); min-width: 232px;
  padding: var(--nw-space-2); position: absolute; right: 0; top: calc(100% + var(--nw-space-2));
  z-index: 40;
}
.nw-menu-panel [role='menuitem'], .nw-menu-panel [role='menuitemradio'] {
  align-items: center; background: none; border: 0; border-radius: var(--nw-radius-md);
  box-sizing: border-box; color: var(--nw-text); cursor: pointer; display: flex;
  font: var(--nw-weight-medium) var(--nw-text-body)/1.2 var(--nw-font); gap: var(--nw-space-2);
  min-height: var(--nw-tap-min); padding: 0 var(--nw-space-3); text-align: left;
  text-decoration: none; width: 100%;
}
.nw-menu-panel [role='menuitem']:hover, .nw-menu-panel [role='menuitemradio']:hover { background: var(--nw-hover); }
.nw-menu-panel [role='menuitem'][aria-current='page'] { background: var(--nw-selected); }
.nw-menu-panel [role='menuitem']:disabled { cursor: not-allowed; opacity: .55; }
.nw-menu-panel [role='menuitemradio'][aria-checked='true']::before { color: var(--nw-accent); content: '✓'; }
.nw-menu-panel [role='menuitemradio'][aria-checked='false']::before { content: '✓'; visibility: hidden; }
.nw-menu-who {
  color: var(--nw-muted); font-size: var(--nw-text-meta); overflow: hidden;
  padding: var(--nw-space-2) var(--nw-space-3) var(--nw-space-3); text-overflow: ellipsis; white-space: nowrap;
}
.nw-menu-rule { background: var(--nw-hairline); border: 0; height: var(--nw-hairline-width); margin: var(--nw-space-2) var(--nw-space-3); }
.nw-menu-label {
  color: var(--nw-faint); font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong);
  letter-spacing: var(--nw-track-label); padding: var(--nw-space-2) var(--nw-space-3); text-transform: uppercase;
}

/**
 * At 390 the navigation leaves the top of the page and becomes a tab bar at the
 * bottom of the screen, where a thumb is — the mark and the account stay in a
 * header that no longer has to hold anything else, and the primary action sits
 * beside the tabs where it is always reachable.
 *
 * Four destinations and the action share the width here: the tabs take what is
 * left after the button, which is what the mockup draws and what eleven of them
 * could not have done.
 */
@media (max-width: 720px) {
  .nw { padding: var(--nw-space-6) var(--nw-space-4) calc(var(--nw-tap-min) + var(--nw-tap-min)); }
  .nw-appbar-inner { gap: var(--nw-space-4); padding: var(--nw-space-3) var(--nw-space-4); }
  .nw-appbar {
    background: var(--nw-card); border-bottom: 0;
  }
  .nw-appbar-go {
    background: var(--nw-card); border-top: var(--nw-hairline-width) solid var(--nw-hairline);
    bottom: 0; box-sizing: border-box; gap: var(--nw-space-2); left: 0;
    padding: 10px var(--nw-space-4) 22px; position: fixed; right: 0; z-index: 30;
  }
  .nw-appbar-end { margin-left: auto; }
  .nw-appbar-lead { margin-left: 0; }
  .nw-appbar-lead .nw-updated { display: none; }
  .nw-appbar-lead .nw-btn {
    align-items: center; display: flex; height: var(--nw-tap-min); justify-content: center;
    min-height: var(--nw-tap-min); padding: 0; width: var(--nw-tap-min);
  }
  .nw-add-mark { display: block; }
  .nw-add-word { clip: rect(0 0 0 0); clip-path: inset(50%); height: 1px; overflow: hidden; position: absolute; white-space: nowrap; width: 1px; }
  .nw-pills {
    flex: 1; gap: 0; justify-content: space-between;
  }
  .nw-pills a {
    align-items: center; background: none; border-radius: 0; display: flex; flex-direction: column;
    font-size: var(--nw-text-chip); font-weight: var(--nw-weight-strong); gap: 5px; padding: 6px 4px 0;
  }
  .nw-subnav-inner { padding: var(--nw-space-4) var(--nw-space-4) 0; }
  .nw-pills a:hover { background: none; color: var(--nw-muted); }
  .nw-pills a[aria-current='page'] { background: none; }
  .nw-pills a[aria-current='page'] { color: var(--nw-accent); }
  .nw-pill-rule { background: transparent; border-radius: var(--nw-radius-full); display: block; height: 3px; width: 22px; }
  .nw-pills a[aria-current='page'] .nw-pill-rule { background: var(--nw-accent); }
  .nw-card { padding: 18px; }
  .nw-card[data-pad='flush'] { --nw-table-inset: 18px; }
  /* A row of controls that cannot wrap scrolls itself rather than the window.
     The range and the figure are two segmented controls on one line, and at 390
     they came to 852px and took the whole page sideways with them — the same
     answer the pill nav and the tab band already give. */
  .nw-filter { min-width: 0; }
  .nw-segmented { max-width: 100%; overflow-x: auto; scrollbar-width: none; }
  .nw-segmented::-webkit-scrollbar { display: none; }
  .nw-figure[data-size='display'] { font-size: var(--nw-text-display-lg); }
  .nw-data { display: block; overflow-x: auto; }
  .nw h1 { overflow-wrap: anywhere; }
  .nw-modal-head, .nw-modal-body, .nw-modal-foot { padding-left: var(--nw-space-5); padding-right: var(--nw-space-5); }
  .nw-fields { flex-wrap: wrap; }
  .nw-field[data-width='narrow'] { width: 100%; }
  .nw-callout--gap { display: none; }
}
`

export const chartCss = `${tokenCss()}\n${BASE_CSS}`
